import { useCallback, useEffect, useRef, useState } from 'react'
import { ErrorObj } from '@adyen/adyen-web/dist/types/core/Errors/types'
import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types'
import { EditPaymentMethodRequest } from 'shared-types'
import { Image } from '~/components/Image'
import { usePage } from '~/hooks'
import { useToast } from '~/hooks/useToast'
import { jayCarFormStyles, rtmFormStyles } from '~/constants/adyenStyles'
import { Button } from '../Button'
import { Dialogue } from '../Dialogue'
import { Icon } from '../Icon'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './CreateCustomerSavedPaymentMethodCardDialogue.module.scss'
import { CreateCustomerSavedPaymentMethodCardDialogueProps } from './CreateCustomerSavedPaymentMethodCardDialogue.types'

const coreConfig: CoreOptions = {
  locale: 'en_AU',
  environment: process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT ?? 'TEST',
  clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
}

export const CreateCustomerSavedPaymentMethodCardDialogue = ({
  isOpen,
  onCancel,
  onConfirm,
  onOk,
}: CreateCustomerSavedPaymentMethodCardDialogueProps) => {
  const paymentContainer = useRef(null)
  const [error, setError] = useState<ErrorObj>(null)
  const [data, setData] = useState<EditPaymentMethodRequest>(null)
  const page = usePage()

  const renderError = useCallback((error) => {
    return (
      <div className='flex gap-4 items-center'>
        <span>
          <Icon
            iconName='alert-triangle-filled'
            size={16}
            color='var(--color-state-alert)'
          />
        </span>
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodySmallBold}
          className='text-alert'>
          {error}
        </Typography>
      </div>
    )
  }, [])

  useEffect(() => {
    if (!paymentContainer.current || !isOpen) {
      return
    }

    const savePaymentMethod = async () => {
      // @ts-ignore
      await import('@adyen/adyen-web/dist/adyen.css')
      const AdyenCheckout = (await import('@adyen/adyen-web')).default
      const checkout = await AdyenCheckout(coreConfig)
      checkout
        .create('securedfields', {
          type: 'card',
          brands: ['mc', 'visa', 'amex', 'bcmc', 'maestro'],
          styles: page.brand === 'rtm' ? rtmFormStyles : jayCarFormStyles,
          autoFocus: true,
          onChange({ errors }) {
            setError(errors)
          },
          onValid({ data }) {
            const {
              encryptedCardNumber,
              encryptedExpiryMonth,
              encryptedExpiryYear,
              encryptedSecurityCode,
            } = data.paymentMethod

            setData({
              encryptedCardNumber,
              encryptedExpiryMonth,
              encryptedExpiryYear,
              encryptedSecurityCode,
            })
          },
        })
        .mount(paymentContainer.current)
    }
    savePaymentMethod()
  }, [isOpen, page.brand])

  useEffect(() => {
    if (!isOpen) {
      setData(null)
      setError(null)
    }
  }, [isOpen])

  const hasError =
    !!error?.encryptedCardNumber ||
    !!error?.encryptedExpiryDate ||
    !!error?.encryptedSecurityCode

  const { showNotification, closeNotification } = useToast()

  const handleAddPayment = async () => {
    if (hasError) {
      return
    }
    const showErrorNotification = () => {
      showNotification({
        children: <span>Invalid Card Details</span>,
        id: 'addPaymentMethodError',
        type: 'error',
        onClose: closeNotification,
      })
    }

    onConfirm.mutate(data, {
      onError: showErrorNotification,
      onSuccess: (data) => {
        if (data) {
          showNotification({
            children: 'Payment Method Added Successfully',
            id: 'addPaymentMethodSuccess',
            type: 'success',
            onClose: closeNotification,
          })
        } else {
          showErrorNotification()
        }
      },
      onSettled: onCancel,
    })
  }

  const isAddPaymentMethodDisabled = !data || hasError

  return (
    <Dialogue
      visible={isOpen}
      onCancel={onCancel}
      onOk={onOk}
      cancelText='Close'
      okText='Confirm'
      wrapperClassName={styles.paymentForm}
      modalClass={styles.paymentModal}>
      <div className={styles.dialogueWrapper}>
        <Typography
          className='text-center text-primary-heading'
          variant={TypographyVariant.BodyXLargeBold}
          tag={TypographyTag.p}>
          Add Payment Method
        </Typography>
        <div className={styles.paymentImages}>
          <Image
            src='/images/icons/mastercard.svg'
            width={34}
            height={24}
            alt='mastercard'
          />
          <Image
            src='/images/icons/visa.svg'
            width={34}
            height={24}
            alt='mastercard'
          />
          <Image
            src='/images/icons/amex.svg'
            width={34}
            height={24}
            alt='mastercard'
          />
        </div>
        <div className={styles.formWrapper} ref={paymentContainer}>
          <div className={styles.inputSection}>
            <div className={styles.inputCard}>
              <Typography
                variant={TypographyVariant.BodySmallBold}
                tag={TypographyTag.p}
                className='text-primary-heading'>
                Card Number
              </Typography>
              <div
                data-cse='encryptedCardNumber'
                className='h-12 rounded border-primary-border border px-2'
              />
              {error?.encryptedCardNumber &&
                renderError(error.encryptedCardNumber.errorMessage)}
            </div>
            <div className={styles.inputs}>
              <div className={styles.inputInner}>
                <Typography
                  className='text-primary-heading'
                  variant={TypographyVariant.BodySmallBold}
                  tag={TypographyTag.p}>
                  Expiry
                </Typography>
                <div
                  data-cse='encryptedExpiryDate'
                  className='h-12 rounded border-primary-border border px-2'
                />
                {error?.encryptedExpiryDate &&
                  renderError(error.encryptedExpiryDate.errorMessage)}
              </div>
              <div className={styles.inputInner}>
                <Typography
                  className='text-primary-heading'
                  variant={TypographyVariant.BodySmallBold}
                  tag={TypographyTag.p}>
                  CVV
                </Typography>
                <div
                  data-cse='encryptedSecurityCode'
                  className='h-12 rounded border-primary-border border px-2'
                />
                {error?.encryptedSecurityCode &&
                  renderError(error.encryptedSecurityCode.errorMessage)}
              </div>
            </div>
          </div>

          <Button
            loading={onConfirm?.isLoading}
            htmlType='submit'
            theme={{ root: 'w-38 flex mx-auto mt-6 p-0' }}
            status='primary'
            disabled={isAddPaymentMethodDisabled}
            onClick={handleAddPayment}>
            Add
          </Button>
        </div>
      </div>
    </Dialogue>
  )
}
